/**
 * SW2-2443 — push a downloaded project's offline work to the server.
 *
 * Moved out of InspectionsDue.jsx, where it ran as two near-copies (inspections
 * already on the server, and inspections created offline) that swallowed most
 * errors and then always reported success. The widget deleted the project's
 * local copy afterwards, so anything that had not uploaded — a connection
 * dropped mid-sync — was destroyed while the UI said "synced".
 *
 * The rules this module keeps:
 *
 *   - A queued write is marked sent only after its request succeeds. Rows that
 *     queue several writes (a finding's create + updates, a certification's
 *     signatures, inspection notes) drop each entry as it lands, so a retry
 *     sends only what is left — never a duplicate.
 *   - A network failure stops the sync. Nothing after it can succeed, and every
 *     request made anyway is one more partial write to reconcile.
 *   - Any other failure (a server rejection) leaves that item queued and moves
 *     on to the rest.
 *   - An inspection created offline is re-keyed to its server id, with
 *     everything recorded against it, before its work is sent. From then on it
 *     is an ordinary synced inspection, so a retry finds whatever is left.
 *
 * The caller decides what to do with the result: delete the local copy only
 * when `remaining` is 0. Sending is in ./offlineSyncSend, findings and their
 * photos in ./offlineFindingSync.
 */
import db, {
  countUnsyncedWork,
  isInspectionAttachment,
  rekeyOfflineInspection,
} from '../db/db';
import inspectionApi from './api/inspection';
import { certificationApi } from './api/certification';
import commentableApi from './api/commentable';
import { fileFromOfflineImage, uploadApi } from './api/upload';
import { positionableApi } from './api/positionable';
import { syncFindings } from './offlineFindingSync';
import {
  ConnectionLost,
  FAILED,
  hasPendingCreate,
  isPending,
  send,
} from './offlineSyncSend';

export { ConnectionLost, FAILED } from './offlineSyncSend';

async function syncAttachments(inspectionId) {
  const pending = await db.images
    .where('payload_id')
    .equals(inspectionId)
    .filter((upload) => !upload.sync.synced && isInspectionAttachment(upload))
    .toArray();

  for (const upload of pending) {
    const label = `attachment ${upload.guid}`;
    // Two requests: upload the file, then link it to the inspection. The upload
    // id is recorded in between, so a link that fails is retried against the
    // same upload rather than uploading the file again.

    let uploadId = upload.sync.uploadId ?? null;

    if (!uploadId) {
      const file = fileFromOfflineImage(upload);

      if (!file) {
        console.error(
          `Skipping inspection attachment ${upload.guid}: no readable offline file data.`,
        );

        continue;
      }

      const uploaded = await send(label, () => uploadApi.create(file));

      if (uploaded === FAILED || !uploaded?.id) continue;

      uploadId = uploaded.id;
      await db.images.update(upload.guid, { 'sync.uploadId': uploadId });
    }

    const linked = await send(label, () =>
      uploadApi.inspection.create(uploadId, inspectionId),
    );

    if (linked === FAILED) continue;

    await db.images.update(upload.guid, { 'sync.synced': true });
  }
}

/**
 * Scoped by project, not start_inspection_id: that field records where a
 * positionable was originally CREATED, so moving or reshaping one from an
 * earlier inspection left it unmatched.
 *
 * Held back until the records it points at exist on the server: a placement of
 * a finding whose create has not landed, or one started on an inspection not
 * yet created, would reach the server carrying a local id.
 */
async function syncPositionables(projectId) {
  const inspections = await db.inspections
    .where('project_id')
    .equals(projectId)
    .toArray();
  const unsentInspections = new Set(
    inspections.filter(isPending).map((i) => i.id),
  );
  const findingIds = inspections
    .map((i) => (i.findings || []).map((f) => f.id))
    .flat();
  const unsentFindings = new Set(
    (await db.findings.bulkGet(findingIds))
      .filter(hasPendingCreate)
      .map((f) => f.id),
  );

  const pending = await db.positionables
    .where('project_id')
    .equals(projectId)
    .filter((p) => !p.sync.synced)
    .toArray();

  // Either end of the inspection window, on the row or in what it will send.
  const waitsOnInspection = (p) =>
    [p, ...(p.sync.payload || [])].some(
      (r) =>
        unsentInspections.has(r.start_inspection_id) ||
        unsentInspections.has(r.end_inspection_id),
    );

  for (const positionable of pending) {
    const type =
      positionable.positionable_type ||
      positionable.sync.payload?.[0]?.positionable_type;

    if (
      waitsOnInspection(positionable) ||
      (type === 'findings' && unsentFindings.has(positionable.positionable_id))
    ) {
      continue;
    }

    const saved = await send(`positionable ${positionable.id}`, () =>
      positionableApi.bulkUpdate(
        positionable.target_type || positionable.tType,
        positionable.target_id,
        positionable.sync.payload,
        { replay: true },
      ),
    );

    if (saved === FAILED) continue;

    // bulkUpdate mirrors the server's rows into db.positionables under their
    // server ids. A placement created offline has a local id the server never
    // saw, so keeping that row as well shows it twice on the offline map — which
    // now matters, because a partial sync keeps the local copy.
    if (
      Array.isArray(saved) &&
      saved.length &&
      !saved.some((row) => row.id === positionable.id)
    ) {
      await db.positionables.delete(positionable.id);
    } else {
      await db.positionables.update(positionable.id, { 'sync.synced': true });
    }
  }
}

async function syncCertification(inspectionId) {
  const certification = await db.certifications.get(inspectionId);

  if (!isPending(certification)) return;

  let queue = [...(certification.sync.payload || [])];

  while (queue.length) {
    const entry = queue[0];
    const sent = await send(`certification for inspection ${inspectionId}`, () =>
      certificationApi.create({ ...entry, inspectionId }, undefined, {
        replay: true,
      }),
    );

    // Marked synced only once every signature has landed. Before SW2-2443 it
    // was marked synced even when the request failed, so the signature was
    // never retried.
    if (sent === FAILED) return;

    queue = queue.slice(1);
    await db.certifications.update(inspectionId, { 'sync.payload': queue });
  }

  await db.certifications.update(inspectionId, { 'sync.synced': true });
}

async function syncNotes(inspectionId) {
  const notes = await db.inspectionNotes.get(inspectionId);

  if (!isPending(notes)) return;

  // Before SW2-2443 the first note on an inspection was queued with no id
  // (commentable.js), so a later edit of it could not be matched to the server
  // note. That note is always the row's first; give queues from earlier
  // releases its id.
  let queue = (notes.sync.payload || []).map((note) =>
    note.method === 'create' && note.id === undefined
      ? { ...note, id: notes.notes?.[0]?.id }
      : note,
  );
  // Local note id → server id, kept on the row so an update queued behind a
  // create still finds its note when a retry resumes between the two.
  const noteIds = { ...(notes.sync.noteIds || {}) };

  while (queue.length) {
    const note = queue[0];
    const label = `note for inspection ${inspectionId}`;

    if (note.method === 'create') {
      const created = await send(label, () =>
        commentableApi.create(
          'inspections',
          inspectionId,
          { note: { content: note.content } },
          { replay: true },
        ),
      );

      if (created === FAILED) return;

      noteIds[note.id] = created?.id;
    } else if (note.method === 'update') {
      const updated = await send(label, () =>
        commentableApi.update(
          'inspections',
          note.id,
          { content: note.content, id: noteIds[note.id] || note.id },
          inspectionId,
          { replay: true },
        ),
      );

      if (updated === FAILED) return;
    } else {
      console.error(`Offline sync: ${label} has a queued "${note.method}" it cannot send`);

      return;
    }

    queue = queue.slice(1);
    await db.inspectionNotes.update(inspectionId, {
      'sync.payload': queue,
      'sync.noteIds': noteIds,
    });
  }

  await db.inspectionNotes.update(inspectionId, { 'sync.synced': true });
}

async function syncQuestionComments(inspectionId) {
  const pending = await db.commentPayload
    .where('inspection_id')
    .equals(inspectionId)
    .filter((comment) => !comment.sync.synced)
    .toArray();

  // One row per comment, marked as each lands — they used to be marked together
  // after the loop, so one failure resent every comment before it.
  for (const comment of pending) {
    const sent = await send(`comment on question ${comment.question_id}`, () =>
      inspectionApi.createQuestionComment(
        comment.question_id,
        inspectionId,
        comment.sync.payload,
        { replay: true },
      ),
    );

    if (sent !== FAILED) {
      await db.commentPayload.update(comment.id, { 'sync.synced': true });
    }
  }
}

async function syncAnswers(inspectionId) {
  const question = await db.questions.get(inspectionId);

  if (!isPending(question)) return;

  const sent = await send(`answers for inspection ${inspectionId}`, () =>
    inspectionApi.update(inspectionId, question.sync.payload, { replay: true }),
  );

  if (sent !== FAILED) {
    await db.questions.update(inspectionId, { 'sync.synced': true });
  }
}

/**
 * Send everything queued for one inspection that exists on the server.
 * Order follows the original sync: answers, findings, attachments, map
 * placements, signatures, notes, comments.
 */
async function syncInspectionWork(inspectionId, projectId, onStatus) {
  const inspection = await db.inspections.get(inspectionId);

  onStatus('Syncing answers and findings...');
  await syncAnswers(inspectionId);
  await syncFindings(inspection);

  onStatus('Uploading attachments and map placements...');
  await syncAttachments(inspectionId);
  await syncPositionables(projectId);

  onStatus('Syncing signatures, notes and comments...');
  await syncCertification(inspectionId);
  await syncNotes(inspectionId);
  await syncQuestionComments(inspectionId);
}

/**
 * @returns {Promise<{ remaining: number, interrupted: boolean }>}
 *   `remaining` — rows of this project still not on the server.
 *   `interrupted` — the sync stopped early because the server was unreachable.
 *   Remaining work with `interrupted` false is work the server refused.
 */
export async function syncOfflineProject(
  projectId,
  { onStatus = () => {}, onProgress = () => {} } = {},
) {
  const project = await db.projects.get(projectId);

  if (!project) throw new Error('Project not found');

  onStatus(`Syncing project ${project.name}...`);

  let interrupted = false;

  try {
    const inspections = await db.inspections
      .where('project_id')
      .equals(projectId)
      .toArray();

    for (const [index, inspection] of inspections.entries()) {
      onStatus(`Syncing inspection ${index + 1}/${inspections.length}...`);

      let inspectionId = inspection.id;

      if (isPending(inspection)) {
        // Recorded as soon as the create lands, so a re-key that fails (an id
        // already taken on this device) is retried against the same server
        // inspection rather than creating another.
        let serverId = inspection.sync.serverId ?? null;

        if (!serverId) {
          const { projectId: payloadProjectId, inspection_types } =
            inspection.sync.payload || {};
          const created = await send(`inspection ${inspection.id}`, () =>
            inspectionApi.create(payloadProjectId, inspection_types, 'offline', {
              replay: true,
            }),
          );

          // Nothing recorded against it can be sent until it exists.
          if (created === FAILED || !created?.id) continue;

          serverId = created.id;
          await db.inspections.update(inspection.id, { 'sync.serverId': serverId });
        }

        await rekeyOfflineInspection(inspection.id, serverId);
        inspectionId = serverId;
      }

      await syncInspectionWork(inspectionId, projectId, onStatus);
      onProgress(Math.round(((index + 1) / inspections.length) * 95));
    }

    // Placements held back until a later inspection or finding was created.
    await syncPositionables(projectId);
  } catch (err) {
    if (!(err instanceof ConnectionLost)) throw err;

    console.error(err.message, err.cause);
    interrupted = true;
  }

  return { remaining: await countUnsyncedWork(projectId), interrupted };
}
